import { useQuery } from '@tanstack/react-query';
import { usersClient } from '@terra/shared-rest-clients-users';
import { getAuthHeaders } from '../utils/authHeaders';
import { getCurrentGatewayUrl } from '../utils/environmentDetection';
import { useSelectedCompany } from '../store/projectStore';
import { getCurrentTenantId } from '../store/tenantStorage';
import { useUserTenants } from './api/useUserTenants';

export interface User {
  id: string;
  name: string;
  email: string;
  avatar?: string;
}

type UseUsersOptions = {
  tenantId?: string | null;
  syncWithSelectedCompanyTenant?: boolean;
};

export const useUsers = ({
  tenantId,
  syncWithSelectedCompanyTenant = true,
}: UseUsersOptions = {}) => {
  const selectedCompany = useSelectedCompany();
  const selectedCompanyTenantId = selectedCompany?.tenantId ?? null;
  const { currentTenantId: authTenantId } = useUserTenants();
  const requestedTenantId =
    tenantId ?? selectedCompanyTenantId ?? authTenantId ?? getCurrentTenantId();

  return useQuery({
    queryKey: ['users', requestedTenantId, syncWithSelectedCompanyTenant],
    queryFn: async () => {
      const effectiveTenantId = requestedTenantId;

      if (
        syncWithSelectedCompanyTenant &&
        selectedCompanyTenantId &&
        effectiveTenantId &&
        selectedCompanyTenantId !== effectiveTenantId
      ) {
        console.warn(
          '[useUsers] Selected company tenant does not match current tenant',
        );
        return [];
      }

      if (!effectiveTenantId) {
        console.warn(
          '[useUsers] No tenant context available - returning empty',
        );
        return [];
      }

      const authHeaders = await getAuthHeaders();

      const response = await usersClient.getUsers({
        query: {
          tenantIds: [effectiveTenantId],
        },
        overrideClientOptions: {
          baseUrl: `${getCurrentGatewayUrl()}/gateway`,
        },
        extraHeaders: authHeaders,
      });

      if (response.status === 200) {
        const users: User[] = response.body.users.map(
          (user: {
            userId: string;
            name?: string;
            email?: string;
            picture?: string;
          }) => ({
            id: user.userId,
            name: user.name || user.email || 'Unknown User',
            email: user.email || '',
            avatar: user.picture,
          }),
        );

        return users;
      }

      throw new Error('Failed to fetch users');
    },
    staleTime: 5 * 60 * 1000,
    retry: 2,
    enabled: !!requestedTenantId,
  });
};
